// import { blue30, blue40, boxShadowRaised, grey5, white } from "@churchofjesuschrist/eden-style-constants";

export const isBrowser = typeof window === "object" && typeof document === "object" && document.nodeType === 9;
export const isUndefined = obj => obj === void 0;
export const isDefined = obj => !isUndefined(obj);
export const isType = (obj, type) => typeof obj === type;
export const isBoolean = obj => obj === true || obj === false;
export const isString = obj => isType(obj, "string");
export const isNumber = obj => isType(obj, "number");
export const isArray = obj => Array.isArray(obj);
export const isArrayOfArray = obj => isArray(obj) && obj.length > 0 && isArray(obj[0]);
export const isArrayOfNotArray = obj => isArray(obj) && (!obj.length || !isArray(obj[0]));
export const isObject = obj => obj === Object(obj);
export const isFunction = obj => isType(obj, "function");
export const isPromise = obj => obj && isFunction(obj.then);
export const isEmpty = value => isUndefined(value) || value === null || value === "" || isNumber(value) && isNaN(value);
export const isNotEmpty = value => !isEmpty(value);
export const isObjectEmpty = obj => !obj || Object.keys(obj).length === 0;
export const isObjectNotEmpty = value => !isObjectEmpty(value);
export const defaultUndefined = (value, replace) => isUndefined(value) ? isUndefined(replace) ? null : resolveValue(replace) : value;
export const defaultEmpty = (value, replace) => isEmpty(value) ? isUndefined(replace) ? null : resolveValue(replace) : value;
export const firstNotEmpty = (obj, ...args) => {
  let props = [];
  for (let prop in obj) {
    props.push(prop);
  }
  if (props.length) {
    let values = args.map(arg => {
      let check = arg.toLowerCase();
      return props.find(prop => prop.toLowerCase() === check);
    }).filter(isNotEmpty).map(prop => obj[prop]).filter(isNotEmpty);
    if (values.length) {
      let value = values[0];
      if (isFunction(value)) {
        value = value.bind(obj);
      }
      return value;
    }
  }
};
export const resolveValue = value => isFunction(value) ? value() : value;
export const resolveFirst = (obj, ...args) => {
  return resolveValue(firstNotEmpty(obj, ...args));
};
export const hasAny = (obj, ...args) => {
  let props = Object.keys(obj);
  if (props.length) {
    return args.map(arg => {
      let check = arg.toLowerCase();
      return props.find(prop => prop.toLowerCase() === check);
    }).filter(isNotEmpty).length > 0;
  }
  return false;
};
export const toBoolean = obj => !isEmpty(obj) && (isString(obj) ? obj === "true" : !!obj);
export const toArray = obj => obj && !isArray(obj) ? [obj] : obj;
export const arrayEquals = (a, b) => {
  return isArray(a) && isArray(b) && a.length === b.length && a.every((val, index) => val === b[index]);
};
export const removeUndefined = obj => {
  Object.keys(obj).forEach(key => isUndefined(obj[key]) && delete obj[key]);
  return obj;
};
export const isNumberEquals = (a, b, tolerance = 0) => {
  return Math.abs(a - b) <= tolerance;
};
export const noop = function () {};
export const getProperty = (obj, path) => {
  if (obj && path) {
    return path.split(".").reduce((value, prop) => value ? value[prop] : null, obj);
  } else {
    return null;
  }
};
export const distinct = list => {
  return list.filter((item, index) => list.indexOf(item) === index);
};
export const capitalize = text => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};
export const camelCase = text => {
  let result = text.replace(/([-_][a-z])/gi, $1 => $1.toUpperCase().replace("-", "").replace("_", ""));
  return result.charAt(0).toLowerCase() + result.slice(1);
};
export const kebabCase = text => {
  return text.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g).map(x => x.toLowerCase()).join("-");
};
export const trimStart = (text, trim) => {
  return text.charAt(0) === trim ? text.slice(1) : text;
};
export const groupBy = (list, prop) => {
  let cb = isFunction(prop);
  return list.reduce((groups, item) => {
    let value = cb ? prop(item) : item[prop];
    (groups[value] = groups[value] || []).push(item);
    return groups;
  }, {});
};

// https://stackoverflow.com/questions/105034/how-to-create-guid-uuid
export const generateGuid = () => {
  if (isBrowser && window.crypto && window.crypto.getRandomValues) {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
  } else {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      let r = Math.random() * 16 | 0,
        v = c === "x" ? r : r & 0x3 | 0x8;
      return v.toString(16);
    });
  }
};
export const hashCode = value => {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    let char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
};
export const queue = (fn, delay = 1) => {
  let timer = null;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, delay);
  };
};
export const delay = (timeout = 1) => {
  return new Promise(resolve => setTimeout(resolve, timeout));
};
export const addListener = (element, eventName, callback, capture = undefined) => {
  element.addEventListener(eventName, callback, capture);
  return {
    remove: () => {
      element.removeEventListener(eventName, callback, capture);
    }
  };
};
export const addListenerOnce = (element, eventName, callback, capture = undefined) => {
  return addListener(element, eventName, callback, {
    once: true,
    capture
  });
};
export const removeListeners = listeners => {
  let execute = !!listeners;
  if (execute) {
    listeners.forEach(listener => {
      listener.remove();
    });
  }
  return execute;
};
export const cancelEvent = event => {
  if (event.stopPropagation) {
    event.stopPropagation();
  }
  if (event.preventDefault) {
    event.preventDefault();
  }
  if (event.stop) {
    event.stop();
  }
  return false;
};
export const proxy = (fn, context) => {
  return (...args) => fn.apply(context || this, args);
};
export const proxyTimeout = (fn, context, delay = 1) => {
  return setTimeout(proxy(fn, context), delay);
};
export const proxyQueue = (fn, context, delay = 1) => {
  return queue(proxy(fn, context), delay);
};
export const enumValue = (value, ...enums) => {
  if (isNotEmpty(value)) {
    for (const current of enums) {
      let entry = Object.entries(current).find(([k, v]) => value === k || value === v);
      if (entry) {
        return entry[1];
      }
    }
  }
  return value;
};
export const testRegex = (regex, text) => {
  regex.lastIndex = 0;
  let test = regex.test(text);
  regex.lastIndex = 0;
  return test;
};
export const blankGif = () => {
  return "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
};
export const classNames = (...names) => {
  return names.filter(name => !!name).join(" ");
};
const compileStyle = (prefix, strings, ...args) => {
  if (isBrowser) {
    let css = "";
    for (let i = 0; i < strings.length; i++) {
      if (i > 0) {
        css += args[i - 1];
      }
      css += strings[i];
    }
    const regex = /&[^}]*}/g;
    const main = css.replace(regex, "");
    const pseudos = css.match(regex);
    let compiled = `${prefix} { ${main} }`;
    if (pseudos) {
      pseudos.forEach(pseudo => {
        compiled += pseudo.replace(/&/g, `\n${prefix}`);
      });
    }
    compiled = compiled.replace(/\s+/g, " ").trim();
    const style = document.createElement("style");
    style.innerHTML = compiled;
    const head = document.getElementsByTagName("head")[0];
    head.appendChild(style);
  }
};
export const styleClass = (className, subClassName = null, extendClassName = null) => {
  const newClass = `maps-${className}${subClassName ? "__" + subClassName : ""}`;
  const fullClass = `.${newClass}${extendClassName ? "." + extendClassName.split(" ").join(".") : ""}`;
  const returnClass = classNames(newClass, extendClassName);
  return (strings, ...args) => {
    compileStyle(fullClass, strings, ...args);
    return returnClass;
  };
};
export const keyframes = name => {
  const newKeyframes = `maps-${name}`;
  return (strings, ...args) => {
    compileStyle(`@keyframes ${newKeyframes}`, strings, ...args);
    return newKeyframes;
  };
};
export const buttonClass = (() => {
  let className = null;
  const initialize = () => {
    if (!className) {
      className = styleClass("button")`
                box-sizing: border-box;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 2.5rem;
                height: 2.5rem;
                font-size: 1.5rem;
                border: 1px solid #e0e2e2;
                border-radius: 0.125rem;
                background: #ffffff;
                margin: 0;
                padding: 0;
                appearance: none;
                outline: none;
                cursor: pointer;
                color: #006184;

                &:hover,
                &:active {
                    color: #003057;
                }
            `;
    }
  };
  return () => {
    initialize();
    return className;
  };
})();
export const controlClass = (() => {
  let className = null;
  const initialize = () => {
    if (!className) {
      className = styleClass("control")`
                box-sizing: border-box;
                margin: 10px;
                box-shadow: 0 2px 4px 0 #00000033;
            `;
    }
  };
  return () => {
    initialize();
    return className;
  };
})();
export const css = (element, styles) => {
  if (element) {
    if (isString(styles)) {
      return element.style[styles];
    } else {
      Object.entries(styles).forEach(([style, value]) => {
        element.style[style] = defaultEmpty(value, "");
      });
    }
  }
};
export const offset = element => {
  let box = element.getBoundingClientRect ? element.getBoundingClientRect() : {
      top: 0,
      left: 0
    },
    docElem = element.ownerDocument.documentElement;
  return {
    top: box.top + (window.pageYOffset || docElem.scrollTop) - (docElem.clientTop || 0),
    left: box.left + (window.pageXOffset || docElem.scrollLeft) - (docElem.clientLeft || 0)
  };
};
export const emptyNode = node => {
  while (node && node.firstChild) {
    node.firstChild.remove();
  }
};
export const toHTML = htmlString => {
  let temp = document.createElement("div");
  temp.innerHTML = htmlString;
  if (temp.childNodes.length === 1) {
    return temp.removeChild(temp.firstChild);
  } else {
    let fragment = document.createDocumentFragment();
    while (temp.firstChild) {
      fragment.appendChild(temp.firstChild);
    }
    return fragment;
  }
};